/** IXD 주석 검증 - 결과 페이지 (`?reportId=`). 진입 때 한 번 검증하고, 미리보기 위에 위치를 보여준다. */

import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import queries from "@/queries";
import { Button } from "@/toggle-ui-kit/button";

import { NoteChecklistView } from "./note-checklist-view";

const MessageScreen = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) => (
  <div className="flex h-screen flex-col items-center justify-center gap-4">
    <p className="body-1-normal-regular text-label-alternative">{message}</p>
    {onRetry && (
      <Button
        variant="outlined"
        color="secondary"
        size="base"
        onClick={onRetry}
      >
        다시 시도
      </Button>
    )}
  </div>
);

export const IxdNoteChecklistViewPage = () => {
  const reportId = useSearchParams()[0].get("reportId");
  if (!reportId) {
    return <MessageScreen message="reportId가 지정되지 않았습니다." />;
  }
  return (
    <Sentry.ErrorBoundary
      fallback={
        <MessageScreen message="주석 검증 결과를 표시하는 중 오류가 발생했습니다." />
      }
    >
      <ViewContent reportId={reportId} />
    </Sentry.ErrorBoundary>
  );
};

const ViewContent = ({ reportId }: { reportId: string }) => {
  const preview = useQuery(queries.ixdEditor(reportId)._ctx.preview());
  const checklist = useQuery(queries.ixdEditor(reportId)._ctx.noteChecklist());

  if (preview.isLoading || checklist.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center gap-2">
        <Loader2 className="w-5 h-5 animate-spin text-label-alternative" />
        <span className="body-1-normal-regular text-label-alternative">
          주석을 검증하는 중...
        </span>
      </div>
    );
  }

  if (!preview.data || !checklist.data) {
    const failed = preview.isError ? preview : checklist;
    return (
      <MessageScreen
        message={
          failed.error instanceof Error
            ? failed.error.message
            : "주석 검증 결과를 불러오는 중 오류가 발생했습니다."
        }
        onRetry={() => void failed.refetch()}
      />
    );
  }

  return (
    <div className="h-full w-full overflow-hidden">
      <NoteChecklistView
        preview={preview.data}
        findings={checklist.data.findings}
      />
    </div>
  );
};
